import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { Moment } from 'moment'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useAppSelector } from 'hooks/reduxHooks'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { isConfiguredFeederOriginAirportCode, rewriteCharterFeederInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'

interface Options {
  offer: App.HotelOffer
  checkIn?: Moment | string
  checkOut?: Moment | string
  rooms?: Array<App.Occupants>
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
  flights?: boolean
  hasFeederFlightPriced?: boolean
  isInclusionsValidityEnabled: boolean
}

export function hasPackageInclusionItems(
  luxPlusInclusions: Array<App.PackageInclusion>,
  inclusions: Array<App.PackageInclusion>,
  bonusInclusions: Array<App.PackageInclusion>,
): boolean {
  return luxPlusInclusions.length > 0 || inclusions.length > 0 || bonusInclusions.length > 0
}

export default function useFlashHotelRateInclusions(pkg: App.Package, options: Options) {
  const {
    offer,
    checkIn,
    checkOut,
    rooms,
    charterBundleRoute,
    flightOrigin,
    flights,
    hasFeederFlightPriced,
    isInclusionsValidityEnabled,
  } = options
  const intl = useIntl()
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn, checkOut })
  const isFeederIncluded = !!offer.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG) &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) &&
    !!hasFeederFlightPriced
  const effectiveInclusions = useMemo(() => {
    if (!isFeederIncluded || !flightOrigin) return inclusions

    const { originAirport, destinationAirport } = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
    return rewriteCharterFeederInclusions(inclusions, {
      feederCity: flightOrigin.name,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [flightOrigin, inclusions, intl, isFeederIncluded])
  const shouldShowInclusionsValidityText = isInclusionsValidityEnabled &&
    !!pkg.showInclusionsValidityTag &&
    hasStructuredPackageInclusionsValidityText(pkg.roomPolicyDescription)
  const visibleInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(effectiveInclusions, shouldShowInclusionsValidityText)
  }, [effectiveInclusions, shouldShowInclusionsValidityText])
  const visibleBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(bonusInclusions, shouldShowInclusionsValidityText)
  }, [bonusInclusions, shouldShowInclusionsValidityText])
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
    offer,
    currency,
    offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
    pkg.price,
  )

  return {
    inclusions: visibleInclusions,
    bonusInclusions: visibleBonusInclusions,
    luxPlusInclusions,
    hasInclusions: hasPackageInclusionItems(luxPlusInclusions, visibleInclusions, visibleBonusInclusions),
    shouldShowInclusionsValidityText,
    selectedOccupancy: rooms?.length === 1 ? rooms[0] : undefined,
    // Forced bundles already have an authored flight inclusion; adding a generic one duplicates it.
    showReturnFlightsInclusion: !!flights && !charterBundleRoute && !offer.bundledWithFlightsOnly,
    showTasteOfGoldBenefit,
  }
}
